import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
// Store
import { useAppSelector } from 'app/hooks/useStore';
// Selectors
import { selectAuthor } from 'app/store/EpisodeAuthors/EpisodeAuthors.selectors';
// Mui
import { Popover, Box, Divider, Typography } from '@mui/material';
// Icons
import {
  InfoOutlined as InfoOutlinedIcon,
  Close as CloseIcon
} from '@mui/icons-material';
// Components
import { IconButton } from 'app/components/Mui';
import { InfoBlock } from 'app/components/Utilities';

const AuthorTPMPopover = () => {
  const { t } = useTranslation();

  const author = useAppSelector(selectAuthor);

  const [ anchorEl, setAnchorEl ] = useState<HTMLButtonElement | null>(null);

  const sortedThirdPartyMatches = useMemo(() => {
    if ( !author || !author.thirdPartyMatches ) return [];

    return [...author.thirdPartyMatches].sort((a, b) => {
      const aDate = a.record?.licenseExpirationDate ? dayjs(a.record.licenseExpirationDate) : null;
      const bDate = b.record?.licenseExpirationDate ? dayjs(b.record.licenseExpirationDate) : null;
  
      if (aDate && bDate) {
        return bDate.valueOf() - aDate.valueOf(); // Descending
      }
      if (aDate && !bDate) return -1;
      if (!aDate && bDate) return 1;
      return 0;
    });
  }, [author]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  if ( !sortedThirdPartyMatches.length ) return null;
  return (
    <Fragment>
      <IconButton
        name="Author third party matches"
        sx={{ width: 28, height: 28 }}
        id="thirdPartyMatchesButton"
        onClick={handleClick}
        size="small"
        edge="end"
      ><InfoOutlinedIcon fontSize="small" /></IconButton>
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ mt: -2.5, ml: 10 }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 4, maxWidth: 360, maxHeight: 440, overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton
              sx={{ mt: -2, mr: -2 }}
              name="Close third party matches"
              onClick={handleClose}
              size="small"
              edge="end"
            ><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>{t('pages.authorRegistry.externalDatasetMatches')}</Typography>
          {sortedThirdPartyMatches.map((match:any, index:number) => {
            const { record, source } = match;

            const recordAddress:string[] = [];
            if ( record?.practiceLocation?.line1 ) recordAddress.push(record.practiceLocation.line1);
            if ( record?.practiceLocation?.city ) recordAddress.push(record.practiceLocation.city);
            if ( record?.practiceLocation?.state ) recordAddress.push(record.practiceLocation.state);

            return (
              <Fragment key={`third part match ${index}`}>
                {source ? (
                  <InfoBlock label={t('labels.source')} value={source} direction="row" />
                ) : null}
                {record.firstName ? (
                  <InfoBlock label={t('labels.firstName')} value={record.firstName} direction="row" />
                ) : null}
                {record.middleName ? (
                  <InfoBlock label={t('labels.middleName')} value={record.middleName} direction="row" />
                ) : null}
                {record.lastName ? (
                  <InfoBlock label={t('labels.lastName')} value={record.lastName} direction="row" />
                ) : null}
                {record.profession ? (
                  <InfoBlock label={t('labels.profession')} value={record.profession} direction="row" />
                ) : null}
                {record.licenseNumber ? (
                  <InfoBlock label={t('labels.licenseNumber')} value={record.licenseNumber} direction="row" />
                ) : null}
                {record.licenseExpirationDate ? (
                  <InfoBlock label={t('labels.licenseExpirationDate')} value={dayjs(record.licenseExpirationDate).format('MMM DD, YYYY')} direction="row" />
                ) : null}
                {record.county ? (
                  <InfoBlock label={t('labels.county')} value={record.county} direction="row" />
                ) : null}
                {recordAddress.length ? (
                  <InfoBlock label={t('labels.address')} value={recordAddress.join(', ')} direction="row" />
                ) : null}
                {sortedThirdPartyMatches.length > 1 && index !== sortedThirdPartyMatches.length - 1 ? (
                  <Divider sx={{ my: 4 }} />
                ) : null}
              </Fragment>
            )
          })}
        </Box>
      </Popover>
    </Fragment>
  )
}

export default AuthorTPMPopover;
